import React, { useLayoutEffect, useRef, useState } from "react";
import { Button, Popover, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import "./CopyableTruncatedCell.css";

export type CopyableTruncatedTrigger = "hover" | "click";

export interface CopyableTruncatedCellProps {
  /** Full value shown in the popover and copied to clipboard. */
  value: string;
  copySuccessMessage?: string;
  /**
   * When to open the popover.
   * ASSUMPTION: hover + click lets users peek and pin for copy/select.
   */
  trigger?: CopyableTruncatedTrigger | CopyableTruncatedTrigger[];
  /** If true, popover only when the preview is truncated. @default true */
  onlyWhenTruncated?: boolean;
  className?: string;
  /** Custom visible content; `value` is still used for copy/popover text. */
  children?: React.ReactNode;
}

/**
 * Shared table cell helper: truncated preview + hover/click popover
 * with selectable full value and a copy button (Google-like).
 * Reuse across Orders, Logs, Catalog, etc.
 */
const CopyableTruncatedCell: React.FC<CopyableTruncatedCellProps> = ({
  value,
  copySuccessMessage = "Valor copiado",
  trigger = ["hover", "click"],
  onlyWhenTruncated = true,
  className,
  children,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const update = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    update();
    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    if (el.parentElement) observer.observe(el.parentElement);
    return () => observer.disconnect();
  }, [value, children]);

  const canOpen = onlyWhenTruncated ? isTruncated : true;

  const handleCopy = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      message.success(copySuccessMessage);
    } catch {
      message.error("No se pudo copiar");
    }
  };

  const triggerNode = (
    <span
      className={[
        "copyable-truncated-cell__trigger",
        canOpen ? "" : "copyable-truncated-cell__trigger--plain",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      title={
        canOpen ? "Pasa el cursor o haz clic para ver y copiar" : undefined
      }
    >
      <span ref={textRef} className="copyable-truncated-cell__preview">
        {children ?? value}
      </span>
    </span>
  );

  return (
    <Popover
      open={canOpen ? open : false}
      onOpenChange={(next) => {
        if (canOpen) setOpen(next);
      }}
      trigger={canOpen ? trigger : []}
      placement="bottomLeft"
      mouseEnterDelay={0.25}
      mouseLeaveDelay={0.35}
      overlayClassName="copyable-truncated-cell__popover"
      content={
        <div className="copyable-truncated-cell__popover-body">
          <span className="copyable-truncated-cell__popover-value">{value}</span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={handleCopy}
            aria-label="Copiar"
            title="Copiar"
          />
        </div>
      }
    >
      {triggerNode}
    </Popover>
  );
};

export default CopyableTruncatedCell;
